//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import {
  BOX_TYPE_BRANDED,
  BOX_TYPE_CLIENT_PROVIDED,
  BOX_TYPE_PLAIN,
  BoxType,
  PACKAGE_INSERT_CLIENT_PROVIDED,
  PACKAGE_INSERT_NONE,
  PackageInsertType,
  SamplingCampaignRequest,
} from '@apis/campaign-requests/sampling/campaign-requests/samplingCampaignRequestsApiModel';
import {
  PurchaseOption,
  SamplingContentType,
  SamplingDeliveryMethod,
  SamplingFulfillmentOption,
  SamplingOpportunityCartonType,
  SamplingOpportunityInsertType,
} from '@apis/campaign-requests/sampling/opportunities/samplingOpportunitiesApiModel';
import { CAMPAIGN_REQUEST_LOCALES } from '@campaigns/constants/CampaignRequestsLocaleConstants';
import {
  BOX_OPTIONS,
  FULFILLMENT_TYPE_DEALS,
  FULFILLMENT_TYPE_SHIPPED,
  WHO_SHIPS_PRODUCT_BAZAARVOICE,
  WHO_SHIPS_PRODUCT_MY_COMPANY,
  getPackageInsertOptions,
} from '@campaigns/constants/sampling/SamplingCampaignRequestConstants';
import { getLocaleDetails } from '@campaigns/utils/sampling/SamplingCampaignRequestUtils';
import { isPurchaseOptionExpired } from '@campaigns/utils/sampling/SamplingCampaignRequestValidationRulesUtils';
import { getContentTypesLabel } from '@campaigns/utils/sampling/samplingCampaignsPurchaseOptionsUtils';
import { isDefined, isUndefined } from '@utils/filterPredicates';

export const isPurchaseOptionExpiredOrZeroUnits = (
  option: PurchaseOption,
): boolean => {
  const isExpired = isPurchaseOptionExpired(option);
  const hasZeroUnits = (option.balance ?? 0) === 0;
  return isExpired || hasZeroUnits;
};

export const isLocaleSupported = (
  fulfillmentOptions: SamplingFulfillmentOption[],
  locale?: string,
): boolean => {
  if (!locale) {
    return true;
  }

  const requestCountry = CAMPAIGN_REQUEST_LOCALES.find(
    (l) => l.value === locale,
  )?.country;
  if (!requestCountry) {
    return false;
  }

  const allAvailableCountryCodes = new Set(
    fulfillmentOptions.flatMap((option) => option.availableCountryCodes ?? []),
  );

  return (
    allAvailableCountryCodes.has(requestCountry) ||
    allAvailableCountryCodes.size === 0
  );
};

export const formatCartonTypeLabel = (cartonType: string): string =>
  cartonType === SamplingOpportunityCartonType.TWO_SIDED_CUSTOM
    ? 'Branded (custom)'
    : cartonType;

const packageInsertToFulfillmentOptionMap: Record<
  PackageInsertType,
  SamplingOpportunityInsertType
> = {
  [PACKAGE_INSERT_CLIENT_PROVIDED]:
    SamplingOpportunityInsertType.CLIENT_SUPPLIED,
  [PACKAGE_INSERT_NONE]: SamplingOpportunityInsertType.NO,
};

const boxTypeToFulfillmentOptionMap: Record<
  BoxType,
  SamplingOpportunityCartonType
> = {
  [BOX_TYPE_BRANDED]: SamplingOpportunityCartonType.TWO_SIDED_CUSTOM,
  [BOX_TYPE_CLIENT_PROVIDED]: SamplingOpportunityCartonType.CLIENT_SUPPLIED,
  [BOX_TYPE_PLAIN]: SamplingOpportunityCartonType.PLAIN,
};

export const isPackageInsertSupported = (
  fulfillmentOptions: SamplingFulfillmentOption[],
  packageInsert?: PackageInsertType,
): boolean => {
  if (isUndefined(packageInsert)) {
    return true;
  }

  const supportedInserts = new Set(
    fulfillmentOptions.map((option) => option.insert).filter(isDefined),
  );

  if (supportedInserts.size === 0) {
    return true;
  }

  const expectedInsert = packageInsertToFulfillmentOptionMap[packageInsert];

  return supportedInserts.has(expectedInsert);
};

export const isBoxTypeSupported = (
  isBazaarvoiceShipped: boolean,
  fulfillmentOptions: SamplingFulfillmentOption[],
  boxType?: BoxType,
): boolean => {
  if (!isBazaarvoiceShipped) {
    return true;
  }

  if (isUndefined(boxType)) {
    return true;
  }

  const supportedBoxTypes = new Set(
    fulfillmentOptions.map((option) => option.cartonType).filter(isDefined),
  );

  if (supportedBoxTypes.size === 0) {
    return true;
  }

  const expectedBoxType = boxTypeToFulfillmentOptionMap[boxType];

  return supportedBoxTypes.has(expectedBoxType);
};

export const hasContentTypeMismatch = (
  formValues: SamplingCampaignRequest,
  purchaseOption: PurchaseOption,
): boolean => {
  const tasks = formValues.memberTasks ?? {};

  if (!purchaseOption.contentType) {
    return false;
  }
  const contentType = purchaseOption.contentType;
  if (contentType === SamplingContentType.REVIEWS_ONLY && tasks.postOnSocial) {
    return true;
  }
  if (contentType === SamplingContentType.SOCIAL_ONLY && tasks.writeReview) {
    return true;
  }
  return false;
};

export enum PrepaidIssueCategory {
  ContentTasks = 'Content Tasks',
  Fulfillment = 'Fulfillment',
}

export interface PrepaidIssueRow {
  /** Human-readable attribute name shown in the table (e.g. "Volume", "Box Type") */
  attribute: string;
  /** What the selected purchase option supports */
  purchaseValue: string;
  /** What the form currently requests (the violation) */
  formValue: string;
  category: PrepaidIssueCategory;
}

export interface PrepaidConfirmationIssues {
  /** opportunityId for each selected purchase option */
  purchaseOptionIds: string[];
  rows: PrepaidIssueRow[];
}

// ---------------------------------------------------------------------------
// Display-value helpers
// ---------------------------------------------------------------------------

// Derived from the existing packageInsertToFulfillmentOptionMap — maps
// SamplingOpportunityInsertType back to the label used in the form UI.
const insertTypeToLabelMap: Record<string, string> = Object.fromEntries(
  Object.entries(packageInsertToFulfillmentOptionMap).map(
    ([packageInsert, insertType]) => [
      insertType,
      getPackageInsertOptions()[packageInsert as PackageInsertType].label,
    ],
  ),
);

// ---------------------------------------------------------------------------
// Per-category issue row builders
// ---------------------------------------------------------------------------

const getBoxTypeIssueRow = (
  formValues: SamplingCampaignRequest,
  fulfillmentOptions: SamplingFulfillmentOption[],
): PrepaidIssueRow | undefined => {
  if (
    isBoxTypeSupported(
      true,
      fulfillmentOptions,
      formValues.fulfillmentOptions?.boxType,
    )
  ) {
    return undefined;
  }

  const supportedCartonTypes = [
    ...new Set(fulfillmentOptions.map((fo) => fo.cartonType).filter(isDefined)),
  ]
    .map(formatCartonTypeLabel)
    .join(', ');

  const formBoxType = formValues.fulfillmentOptions?.boxType;

  return {
    attribute: 'Package Type',
    purchaseValue: supportedCartonTypes || 'Unknown',
    formValue: formBoxType ? BOX_OPTIONS[formBoxType].label : 'Unknown',
    category: PrepaidIssueCategory.Fulfillment,
  };
};

const getPackageInsertIssueRow = (
  formValues: SamplingCampaignRequest,
  fulfillmentOptions: SamplingFulfillmentOption[],
): PrepaidIssueRow | undefined => {
  if (
    isPackageInsertSupported(
      fulfillmentOptions,
      formValues.fulfillmentOptions?.packageInsert,
    )
  ) {
    return undefined;
  }

  const supportedInserts = [
    ...new Set(
      fulfillmentOptions
        .map((fo) => fo.insert)
        .filter(isDefined)
        .map((ins) => insertTypeToLabelMap[ins] ?? ins),
    ),
  ].join(', ');

  const formInsert = formValues.fulfillmentOptions?.packageInsert;

  return {
    attribute: 'Package Insert',
    purchaseValue: supportedInserts || 'Unknown',
    formValue: formInsert
      ? getPackageInsertOptions()[formInsert].label
      : 'Unknown',
    category: PrepaidIssueCategory.Fulfillment,
  };
};

const getLocaleIssueRow = (
  formValues: SamplingCampaignRequest,
  fulfillmentOptions: SamplingFulfillmentOption[],
): PrepaidIssueRow | undefined => {
  if (isLocaleSupported(fulfillmentOptions, formValues.locale)) {
    return undefined;
  }

  const supportedCountries = [
    ...new Set(
      fulfillmentOptions.flatMap((fo) => fo.availableCountryCodes ?? []),
    ),
  ].join(', ');

  const requestCountry =
    getLocaleDetails(formValues.locale)?.country ?? 'Unknown';

  return {
    attribute: 'Country',
    purchaseValue: supportedCountries || 'Unknown',
    formValue: requestCountry,
    category: PrepaidIssueCategory.Fulfillment,
  };
};

const getFulfillmentIssueRows = (
  formValues: SamplingCampaignRequest,
  primaryOption: PurchaseOption,
  purchaseOptionCount: number,
): PrepaidIssueRow[] => {
  const isBazaarvoiceShipped =
    formValues.fulfillmentOptions?.whoShipsProduct ===
    WHO_SHIPS_PRODUCT_BAZAARVOICE;

  if (!isBazaarvoiceShipped) {
    return [];
  }

  // For multiple POs the compatibility check guarantees each PO has exactly
  // one fulfillmentOptions entry that is identical across POs, so only the
  // first entry is needed. For a single PO, validate against all entries so
  // that per-country fulfillment options (e.g. one row for US, one for UK)
  // are all considered.
  const allFulfillmentOptions =
    purchaseOptionCount > 1
      ? (primaryOption.fulfillmentOptions ?? []).slice(0, 1)
      : primaryOption.fulfillmentOptions ?? [];

  const requestCountry = getLocaleDetails(formValues.locale)?.country;
  const localeFilteredOptions = requestCountry
    ? allFulfillmentOptions.filter((fo) =>
        (fo.availableCountryCodes ?? []).includes(requestCountry),
      )
    : allFulfillmentOptions;

  return [
    getBoxTypeIssueRow(formValues, localeFilteredOptions),
    getPackageInsertIssueRow(formValues, localeFilteredOptions),
    getLocaleIssueRow(formValues, localeFilteredOptions),
  ].filter(isDefined);
};

const getContentTypesIssueRow = (
  formValues: SamplingCampaignRequest,
  primaryOption: PurchaseOption,
): PrepaidIssueRow | undefined => {
  if (!primaryOption.contentType) {
    return undefined;
  }

  if (
    !hasContentTypeMismatch(formValues, primaryOption) &&
    !hasPostSurveyMismatch(formValues, primaryOption)
  ) {
    return undefined;
  }

  const tasks = formValues.memberTasks ?? {};
  const formContentTypes = [
    tasks.writeReview ? 'Reviews' : null,
    tasks.postOnSocial ? 'Social' : null,
    tasks.postSurvey ? 'Post Survey' : null,
  ]
    .filter(Boolean)
    .join(', ');

  return {
    attribute: 'Content Types',
    purchaseValue: getContentTypesLabel(primaryOption) || 'Unknown',
    formValue: formContentTypes || 'Unknown',
    category: PrepaidIssueCategory.ContentTasks,
  };
};

// ---------------------------------------------------------------------------

export const getCreatePrepaidConfirmationIssues = (
  formValues: SamplingCampaignRequest,
  isSamplingPrepaidCampaignsEnabled: boolean,
): PrepaidConfirmationIssues => {
  const empty: PrepaidConfirmationIssues = { purchaseOptionIds: [], rows: [] };

  if (!isSamplingPrepaidCampaignsEnabled || formValues.isNewPurchase) {
    return empty;
  }

  const purchaseOptions = formValues.purchaseOption ?? [];
  const [primaryOption] = purchaseOptions;
  if (!primaryOption) {
    return empty;
  }

  const purchaseOptionIds = purchaseOptions.map((opt) => opt.opportunityId);

  const rows: PrepaidIssueRow[] = [
    getContentTypesIssueRow(formValues, primaryOption),
    ...getFulfillmentIssueRows(
      formValues,
      primaryOption,
      purchaseOptions.length,
    ),
  ].filter(isDefined);

  return { purchaseOptionIds, rows };
};

export const hasPostSurveyMismatch = (
  formValues: SamplingCampaignRequest,
  purchaseOption: PurchaseOption,
): boolean => {
  const isPostSurveySelected = formValues.memberTasks?.postSurvey ?? false;
  const hasPostSurvey = purchaseOption.hasPostSurvey ?? false;

  return isPostSurveySelected && !hasPostSurvey;
};
